import type { MetricGridViewData } from '@shared/types/views';
import { TrendIndicator } from '@/components/views/atoms/TrendIndicator';
import { ToneDot } from '@/components/views/atoms/ToneDot';

/** Numbers read the French way, with at most two decimals (1 234 567,89). */
export const formatMetricValue = (value: string | number): string =>
  typeof value === 'number' ? value.toLocaleString('fr-FR', { maximumFractionDigits: 2 }) : value;

/** Columns that never leave a lone card: 4 → 2×2 or 4, 5-6 → 3, 7-8 → 4. */
export const metricGridColumns = (count: number): string => {
  if (count <= 1) return '';
  if (count === 2) return 'cq-xs:grid-cols-2';
  if (count === 3 || count === 5 || count === 6 || count === 9) return 'cq-xs:grid-cols-2 cq-md:grid-cols-3';
  return 'cq-xs:grid-cols-2 cq-md:grid-cols-4';
};

interface MetricGridViewProps {
  hideTitle?: boolean;
  data: MetricGridViewData;
}

/**
 * Band layout by count: up to 3 figures always stay on one line; 4 to 6 go two
 * per line in a narrow space and on one line from the medium width. Rules
 * separate the figures of a line, never the first one.
 */
const bandLayout = (count: number): { grid: string; item: string } => {
  if (count <= 3) {
    return { grid: ['', '', 'grid-cols-2', 'grid-cols-3'][count] ?? '', item: '[&:not(:first-child)]:border-l' };
  }
  const wide = { 4: 'cq-md:grid-cols-4', 5: 'cq-md:grid-cols-5' }[count] ?? 'cq-md:grid-cols-6';
  return { grid: `grid-cols-2 ${wide}`, item: '[&:nth-child(2n)]:border-l cq-md:[&:not(:first-child)]:border-l' };
};

/**
 * Headline band: the key figures of a result in one light strip, separated by
 * thin rules, wrapping to two per line when its space is narrow.
 */
const MetricBand = ({ data, hideTitle }: MetricGridViewProps) => (
  <div className="min-w-0">
    {!hideTitle && data.title && <p className="text-sm font-semibold text-domino-ink">{data.title}</p>}
    {data.source_note && <p className="mt-1 text-xs leading-5 text-domino-inkSoft">{data.source_note}</p>}
    <dl data-view-surface className={`mt-2 grid gap-y-3 rounded-xl border border-domino-cardBorder bg-domino-warm py-3 ${bandLayout(data.metrics.length).grid}`}>
      {data.metrics.map((metric, index) => (
        <div key={`${metric.label}-${index}`} className={`min-w-0 border-domino-cardBorder px-4 ${bandLayout(data.metrics.length).item}`}>
          <dt className="flex items-center gap-1.5 text-xs text-domino-inkSoft">
            {metric.status && metric.status !== 'neutral' && <ToneDot tone={metric.status} />}
            <span className="truncate" title={metric.label}>{metric.label}</span>
          </dt>
          <dd className="mt-0.5 flex flex-wrap items-baseline gap-x-2">
            <span className="text-2xl font-semibold tabular-nums text-domino-ink">
              {formatMetricValue(metric.value)}{metric.unit ? `\u00a0${metric.unit}` : ''}
            </span>
            {metric.trend && <TrendIndicator trend={metric.trend} value={metric.trend_value} />}
          </dd>
        </div>
      ))}
    </dl>
  </div>
);

export const MetricGridView = ({ data, hideTitle }: MetricGridViewProps) => {
  if (data.presentation === 'band') return <MetricBand data={data} hideTitle={hideTitle} />;
  return (
    <div data-view-surface className="rounded-xl border border-domino-cardBorder bg-domino-warm p-4">
      {!hideTitle && data.title && <p className="text-sm font-semibold text-domino-ink">{data.title}</p>}

      {data.source_note && <p className="mt-1 text-xs leading-5 text-domino-inkSoft">{data.source_note}</p>}
      <div className={`mt-3 grid gap-x-6 gap-y-4 ${metricGridColumns(data.metrics.length)}`}>
        {data.metrics.map((metric) => (
          <div key={`${metric.label}-${String(metric.value)}`} className="min-w-0">
            <p className="flex items-center gap-1.5 text-xs text-domino-inkSoft">
              {metric.status && metric.status !== 'neutral' && <ToneDot tone={metric.status} />}
              <span className="truncate" title={metric.label}>{metric.label}</span>
            </p>
            <p className="mt-0.5 flex flex-wrap items-baseline gap-x-2">
              <span className="text-2xl font-semibold tabular-nums text-domino-ink">
                {formatMetricValue(metric.value)}{metric.unit ? `\u00a0${metric.unit}` : ''}
              </span>
              {metric.trend && <TrendIndicator trend={metric.trend} value={metric.trend_value} />}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};
